import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import {
  consumerApi,
  endConsumerSession,
  establishConsumerSession,
  refreshCsrfToken,
  resetConsumerApiAuthState,
} from "@/lib/consumerApi";

// SecureShield Pro commerce remains isolated in the standalone consumer service,
// but identity is always the canonical LightningFlow website account. The
// browser bridge creates the local service session without another login form.
const SecureMacProAccountContext = createContext(null);

const SIGNED_OUT = {
  account: false,
  accountError: "",
  entitlement: null,
  secureShieldEntitlement: null,
  secureShieldDevices: [],
  secureShieldDevicesError: "",
  entitlementError: "",
  devices: [],
  devicesError: "",
  purchases: [],
  purchasesError: "",
  secureAIEntitlement: null,
  secureAIEntitlementError: "",
  haloEntitlement: null,
  secureAIDevices: [],
  secureAIDevicesError: "",
};

export function SecureMacProAccountProvider({ children }) {
  const { customer, ready: canonicalReady } = useCustomerAuth();
  const canonicalCustomerId = customer && customer.id ? String(customer.id) : "";
  const canonicalEmail = customer && customer.email ? String(customer.email) : "";
  const requestSequence = useRef(0);
  // account: null = loading, false = signed out, object = canonical customer.
  const [state, setState] = useState({
    account: null,
    accountError: "",
    entitlement: null,
    secureShieldEntitlement: null,
    secureShieldDevices: [],
    secureShieldDevicesError: "",
    entitlementError: "",
    devices: [],
    devicesError: "",
    purchases: [],
    purchasesError: "",
    secureAIEntitlement: null,
    secureAIEntitlementError: "",
    haloEntitlement: null,
    secureAIDevices: [],
    secureAIDevicesError: "",
  });

  const refresh = useCallback(async () => {
    const requestId = ++requestSequence.current;
    const commit = (next) => {
      if (requestSequence.current === requestId) setState(next);
    };

    if (!canonicalReady) {
      commit({
        account: null,
        accountError: "",
        entitlement: null,
        secureShieldEntitlement: null,
        secureShieldDevices: [],
        secureShieldDevicesError: "",
        entitlementError: "",
        devices: [],
        devicesError: "",
        purchases: [],
        purchasesError: "",
        secureAIEntitlement: null,
        secureAIEntitlementError: "",
        haloEntitlement: null,
        secureAIDevices: [],
        secureAIDevicesError: "",
      });
      return;
    }
    if (!canonicalCustomerId) {
      resetConsumerApiAuthState();
      commit(SIGNED_OUT);
      return;
    }

    const canonicalAccount = {
      customer_id: canonicalCustomerId,
      email: canonicalEmail,
      email_verified: true,
    };
    setState((previous) =>
      previous.account?.customer_id === canonicalCustomerId
        ? { ...previous, accountError: "" }
        : {
            account: null,
            accountError: "",
            entitlement: null,
            secureShieldEntitlement: null,
            secureShieldDevices: [],
            secureShieldDevicesError: "",
            entitlementError: "",
            devices: [],
            devicesError: "",
            purchases: [],
            purchasesError: "",
            secureAIEntitlement: null,
            secureAIEntitlementError: "",
            haloEntitlement: null,
            secureAIDevices: [],
            secureAIDevicesError: "",
          },
    );

    try {
      let session;
      try {
        ({ data: session } = await consumerApi.get("/v1/session/me"));
      } catch (error) {
        if (error?.response?.status !== 401) throw error;
        session = await establishConsumerSession();
      }

      if (String(session.customer_id) !== canonicalCustomerId) {
        await endConsumerSession();
        session = await establishConsumerSession();
      }
      if (String(session.customer_id) !== canonicalCustomerId) {
        throw new Error("Consumer session does not match the canonical customer");
      }

      try {
        await refreshCsrfToken();
      } catch {
        // Mutating calls fail closed without a CSRF token; the UI reports it.
      }
      const [entitlementsResult, devicesResult, secureAIDevicesResult, purchasesResult, secureShieldDevicesResult] = await Promise.allSettled([
        consumerApi.get("/v1/entitlements"),
        consumerApi.get("/v1/licenses/devices"),
        consumerApi.get("/v1/devices", { params: { product: "secureai" } }),
        consumerApi.get("/v1/purchases"),
        consumerApi.get("/v1/licenses/devices", { params: { product: "secureshield" } }),
      ]);
      const entitlements = entitlementsResult.status === "fulfilled"
        ? entitlementsResult.value.data?.entitlements || []
        : [];
      const entitlementFor = (product) => {
        const found = entitlements.find((item) => item.product === product);
        return found || { product, status: "none", active: false };
      };
      commit({
        account: canonicalAccount,
        accountError: "",
        entitlement: entitlementsResult.status === "fulfilled" ? entitlementFor("securemac-pro") : null,
        secureShieldEntitlement: entitlementsResult.status === "fulfilled" ? entitlementFor("secureshield") : null,
        secureShieldDevices: secureShieldDevicesResult.status === "fulfilled"
          ? (secureShieldDevicesResult.value.data?.devices || []).filter((device) => device.product === "secureshield") : [],
        secureShieldDevicesError: secureShieldDevicesResult.status === "rejected" ? "unavailable" : "",
        entitlementError: entitlementsResult.status === "rejected" ? "unavailable" : "",
        devices:
          devicesResult.status === "fulfilled" ? devicesResult.value.data?.devices || [] : [],
        devicesError: devicesResult.status === "rejected" ? "unavailable" : "",
        purchases:
          purchasesResult.status === "fulfilled" ? purchasesResult.value.data?.purchases || [] : [],
        purchasesError: purchasesResult.status === "rejected" ? "unavailable" : "",
        secureAIEntitlement:
          entitlementsResult.status === "fulfilled" ? entitlementFor("secureai") : null,
        secureAIEntitlementError:
          entitlementsResult.status === "rejected" ? "unavailable" : "",
        // Consumer-platform Halo subscription (entitlement id "halo").
        haloEntitlement:
          entitlementsResult.status === "fulfilled" ? entitlementFor("halo") : null,
        secureAIDevices:
          secureAIDevicesResult.status === "fulfilled"
            ? secureAIDevicesResult.value.data?.devices || []
            : [],
        secureAIDevicesError:
          secureAIDevicesResult.status === "rejected" ? "unavailable" : "",
      });
    } catch {
      resetConsumerApiAuthState();
      commit({
        account: canonicalAccount,
        accountError: "unavailable",
        entitlement: null,
        secureShieldEntitlement: null,
        secureShieldDevices: [],
        secureShieldDevicesError: "",
        entitlementError: "",
        devices: [],
        devicesError: "",
        purchases: [],
        purchasesError: "",
        secureAIEntitlement: null,
        secureAIEntitlementError: "",
        haloEntitlement: null,
        secureAIDevices: [],
        secureAIDevicesError: "",
      });
    }
  }, [canonicalCustomerId, canonicalEmail, canonicalReady]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    requestSequence.current += 1;
    try {
      await endConsumerSession();
    } catch {
      // The canonical website logout still proceeds; local auth state is cleared.
    } finally {
      resetConsumerApiAuthState();
      setState(SIGNED_OUT);
    }
  }, []);

  const releaseDevice = useCallback(
    async (deviceId) => {
      await consumerApi.delete(`/v1/licenses/devices/${deviceId}`, { data: {} });
      await refresh();
    },
    [refresh],
  );

  const releaseSecureAIDevice = useCallback(
    async (deviceId) => {
      await consumerApi.delete(`/v1/devices/${deviceId}`);
      await refresh();
    },
    [refresh],
  );

  const value = useMemo(
    () => ({
      account: state.account,
      accountError: state.accountError,
      entitlement: state.entitlement,
      secureShieldEntitlement: state.secureShieldEntitlement,
      secureShieldDevices: state.secureShieldDevices,
      secureShieldDevicesError: state.secureShieldDevicesError,
      entitlementError: state.entitlementError,
      devices: state.devices,
      devicesError: state.devicesError,
      purchases: state.purchases,
      purchasesError: state.purchasesError,
      secureAIEntitlement: state.secureAIEntitlement,
      secureAIEntitlementError: state.secureAIEntitlementError,
      haloEntitlement: state.haloEntitlement,
      secureAIDevices: state.secureAIDevices,
      secureAIDevicesError: state.secureAIDevicesError,
      ready: state.account !== null,
      owned: Boolean(state.entitlement?.active),
      refresh,
      logout,
      releaseDevice,
      releaseSecureAIDevice,
    }),
    [state, refresh, logout, releaseDevice, releaseSecureAIDevice],
  );

  return (
    <SecureMacProAccountContext.Provider value={value}>{children}</SecureMacProAccountContext.Provider>
  );
}

export function useSecureMacProAccount() {
  const ctx = useContext(SecureMacProAccountContext);
  if (!ctx) throw new Error("useSecureMacProAccount must be used inside SecureMacProAccountProvider");
  return ctx;
}
